import { useContext, useCallback, useEffect, useMemo, useRef } from 'react';
import { useMutation, gql } from '@apollo/client';
import { UserContext } from '../userProvider';
import { AuthContext } from '../authenticationProvider';

const UPDATE_USER_SETTINGS = gql`
  mutation updateCurrentUserSettings($settings: updateCurrentUserSettingsInput!) {
    updateCurrentUserSettings(settings: $settings) {
      message
      responseCode
    }
  }
`;

const RECENT_PARTICIPANTS_LIMIT = 10;

const DEFAULT_PARTICIPANT_MERGE_SEARCH_SETTINGS = {
    matchOn: ['ssn', 'champid', 'dob'],
    name: '',
    dob: '',
    ssn: '',
    cid: '',
    champid: '',
    fin: '',
    booking: '',
    fullNameSearch: false,
    pageSize: 25,
};

type RecentParticipantVisit = {
    participantId?: number | null;
    odrNumber: number;
    name?: string | null;
    visitedAt?: string | null;
}

type ParticipantMergeSearchSettings = {
    matchOn: string[];
    name: string;
    dob: string;
    ssn: string;
    cid: string;
    champid: string;
    fin: string;
    booking: string;
    fullNameSearch: boolean;
    pageSize: number;
}

type EcmDashboardColumnPreference = {
    page: string;
    visibleColumns: string[];
}

export type CentralPlannerPreferences = {
    fullView: boolean;
    focusColumns: string[] | null;
    fullColumns: string[] | null;
}

const toNumber = (value: any): number | null => {
    if (typeof value === 'number' && !Number.isNaN(value)) {
        return value;
    }

    if (typeof value === 'string') {
        const parsed = parseInt(value, 10);
        if (!Number.isNaN(parsed)) {
            return parsed;
        }
    }

    return null;
};

const coerceStringList = (value: any): string[] => {
    if (!Array.isArray(value)) {
        return [];
    }

    return Array.from(new Set(
        value
            .filter((item: any) => typeof item === 'string')
            .map((item: string) => item.trim())
            .filter(Boolean)
    ));
};

const normalizeRecentParticipantEntry = (entry: any): RecentParticipantVisit | null => {
    if (!entry) {
        return null;
    }

    if (typeof entry === 'number' || typeof entry === 'string') {
        const odr = toNumber(entry);
        if (odr == null) {
            return null;
        }

        return {
            participantId: null,
            odrNumber: odr,
            name: null,
            visitedAt: null
        };
    }

    if (typeof entry !== 'object') {
        return null;
    }

    const odr = toNumber(entry.odrNumber ?? entry.participantId);
    if (odr == null) {
        return null;
    }

    return {
        participantId: typeof entry.participantId === 'number' ? entry.participantId : null,
        odrNumber: odr,
        name: typeof entry.name === 'string' ? entry.name : null,
        visitedAt: typeof entry.visitedAt === 'string' ? entry.visitedAt : null
    };
};

const coerceRecentParticipants = (entries: any): RecentParticipantVisit[] => {
    if (!Array.isArray(entries)) {
        return [];
    }

    return entries
        .map(normalizeRecentParticipantEntry)
        .filter((entry): entry is RecentParticipantVisit => Boolean(entry));
};

const coerceParticipantMergeSearchSettings = (settings: any): ParticipantMergeSearchSettings => {
    if (!settings || typeof settings !== 'object') {
        return DEFAULT_PARTICIPANT_MERGE_SEARCH_SETTINGS;
    }

    return {
        matchOn: Array.isArray(settings.matchOn)
            ? Array.from(new Set(settings.matchOn.filter((item: any) => typeof item === 'string' && item.trim())))
            : DEFAULT_PARTICIPANT_MERGE_SEARCH_SETTINGS.matchOn,
        name: typeof settings.name === 'string' ? settings.name : '',
        dob: typeof settings.dob === 'string' ? settings.dob : '',
        ssn: typeof settings.ssn === 'string' ? settings.ssn : '',
        cid: typeof settings.cid === 'string' ? settings.cid : '',
        champid: typeof settings.champid === 'string' ? settings.champid : '',
        fin: typeof settings.fin === 'string' ? settings.fin : '',
        booking: typeof settings.booking === 'string' ? settings.booking : '',
        fullNameSearch: Boolean(settings.fullNameSearch),
        pageSize: typeof settings.pageSize === 'number' && settings.pageSize > 0
            ? Math.min(Math.floor(settings.pageSize), 100)
            : DEFAULT_PARTICIPANT_MERGE_SEARCH_SETTINGS.pageSize,
    };
};

const coerceEcmDashboardColumnPreferences = (settings: any): EcmDashboardColumnPreference[] => {
    if (!Array.isArray(settings)) {
        return [];
    }

    const preferencesByPage = settings.reduce((accumulator: Record<string, EcmDashboardColumnPreference>, entry: any) => {
        if (!entry || typeof entry !== 'object') {
            return accumulator;
        }

        const page = typeof entry.page === 'string' ? entry.page.trim() : '';
        if (!page) {
            return accumulator;
        }

        accumulator[page] = {
            page,
            visibleColumns: coerceStringList(entry.visibleColumns)
        };

        return accumulator;
    }, {});

    return Object.values(preferencesByPage);
};

const coerceCentralPlannerColumns = (value: any): string[] | null => {
    if (!Array.isArray(value)) {
        return null;
    }

    return coerceStringList(value)
        .filter((column) => /^[A-Za-z][A-Za-z0-9]{0,40}$/.test(column))
        .slice(0, 60);
};

const coerceCentralPlannerPreferences = (settings: any): CentralPlannerPreferences => ({
    fullView: Boolean(settings?.fullView),
    focusColumns: coerceCentralPlannerColumns(settings?.focusColumns),
    fullColumns: coerceCentralPlannerColumns(settings?.fullColumns),
});

export const useUser = () => {
    const { setAuthenticationStateJWT } = useContext(AuthContext) as any;
    const { user, setUser, isUserLoading, setIsUserLoading } = useContext(UserContext);

    const userRef = useRef(user);
    const setUserRef = useRef(setUser);

    const recentParticipants = useMemo(() => {
        if (Array.isArray(user?.recentParticipants) && user.recentParticipants.length > 0) {
            return coerceRecentParticipants(user.recentParticipants);
        }

        return coerceRecentParticipants(user?.settings?.recentParticipants);
    }, [user]);

    const participantMergeSearchSettings = useMemo(() => {
        return coerceParticipantMergeSearchSettings(user?.settings?.participantMergeSearchSettings);
    }, [user?.settings?.participantMergeSearchSettings]);

    const ecmDashboardColumnPreferences = useMemo(() => {
        return coerceEcmDashboardColumnPreferences(user?.settings?.ecmDashboardColumnPreferences);
    }, [user?.settings?.ecmDashboardColumnPreferences]);

    const ecmDashboardColumnPreferenceMap = useMemo(() => {
        return ecmDashboardColumnPreferences.reduce((accumulator: Record<string, string[]>, entry) => {
            accumulator[entry.page] = entry.visibleColumns;
            return accumulator;
        }, {});
    }, [ecmDashboardColumnPreferences]);

    const centralPlannerPreferences = useMemo(() => {
        return coerceCentralPlannerPreferences(user?.settings?.centralPlannerPreferences);
    }, [user?.settings?.centralPlannerPreferences]);

    const normalizedUser = useMemo(() => {
        if (!user) {
            return user;
        }

        return {
            ...user,
            recentParticipants,
            settings: {
                ...(user.settings || {}),
                ecmDashboardColumnPreferences,
                centralPlannerPreferences,
                participantMergeSearchSettings,
                recentParticipants
            }
        };
    }, [user, centralPlannerPreferences, ecmDashboardColumnPreferences, recentParticipants, participantMergeSearchSettings]);

    useEffect(() => {
        userRef.current = normalizedUser || {};
    }, [normalizedUser]);

    useEffect(() => {
        setUserRef.current = setUser;
    }, [setUser]);

    const [updateUserSettingsMutation] = useMutation(UPDATE_USER_SETTINGS);

    const logout = () => {
        setAuthenticationStateJWT('');
        setUser({});
    }

    const updateAdminFavorites = async (favorites: string[]) => {
        // Update local user state immediately for responsive UI
        const baseUser = normalizedUser || {};
        setUser({
            ...baseUser,
            settings: {
                ...(baseUser.settings || {}),
                adminSettingsFavorites: favorites
            }
        });

        try {
            const result = await updateUserSettingsMutation({
                variables: {
                    settings: {
                        adminSettingsFavorites: favorites
                    }
                }
            });

            if (result.data?.updateCurrentUserSettings?.responseCode === 200) {
                return { success: true };
            }

            return { success: false, error: result.data?.updateCurrentUserSettings?.message };
        } catch (error) {
            console.error('Error updating admin favorites:', error);
            return { success: false, error: 'Failed to update favorites' };
        }
    }

    const updateSystemSettingsFavorites = async (favorites: string[]) => {
        // Update local user state immediately for responsive UI
        const baseUser = normalizedUser || {};
        setUser({
            ...baseUser,
            settings: {
                ...(baseUser.settings || {}),
                systemSettingsFavorites: favorites
            }
        });

        try {
            const result = await updateUserSettingsMutation({
                variables: {
                    settings: {
                        systemSettingsFavorites: favorites
                    }
                }
            });

            if (result.data?.updateCurrentUserSettings?.responseCode === 200) {
                return { success: true };
            }

            return { success: false, error: result.data?.updateCurrentUserSettings?.message };
        } catch (error) {
            console.error('Error updating system settings favorites:', error);
            return { success: false, error: 'Failed to update favorites' };
        }
    }

    const updateDashboardPreference = async (dashboard: 'legacy' | 'DD2') => {
        const baseUser = normalizedUser || {};
        setUser({
            ...baseUser,
            settings: {
                ...(baseUser.settings || {}),
                dashboard
            }
        });

        try {
            const result = await updateUserSettingsMutation({
                variables: {
                    settings: {
                        dashboard
                    }
                }
            });

            if (result.data?.updateCurrentUserSettings?.responseCode === 200) {
                return { success: true };
            }

            return { success: false, error: result.data?.updateCurrentUserSettings?.message };
        } catch (error) {
            console.error('Error updating dashboard preference:', error);
            return { success: false, error: 'Failed to update dashboard preference' };
        }
    }

    const updateReportFavorites = async (favorites: string[]) => {
        // Update local user state immediately for responsive UI
        const baseUser = normalizedUser || {};
        setUser({
            ...baseUser,
            settings: {
                ...(baseUser.settings || {}),
                reportFavorites: favorites
            }
        });

        try {
            const result = await updateUserSettingsMutation({
                variables: {
                    settings: {
                        reportFavorites: favorites
                    }
                }
            });

            if (result.data?.updateCurrentUserSettings?.responseCode === 200) {
                return { success: true };
            }

            return { success: false, error: result.data?.updateCurrentUserSettings?.message };
        } catch (error) {
            console.error('Error updating report favorites:', error);
            return { success: false, error: 'Failed to update favorites' };
        }
    }

    const updateParticipantMergeSearchSettings = async (settings: ParticipantMergeSearchSettings) => {
        const normalizedSettings = coerceParticipantMergeSearchSettings(settings);
        const baseUser = normalizedUser || {};
        setUser({
            ...baseUser,
            settings: {
                ...(baseUser.settings || {}),
                participantMergeSearchSettings: normalizedSettings
            }
        });

        try {
            const result = await updateUserSettingsMutation({
                variables: {
                    settings: {
                        participantMergeSearchSettings: normalizedSettings
                    }
                }
            });

            if (result.data?.updateCurrentUserSettings?.responseCode === 200) {
                return { success: true };
            }

            return { success: false, error: result.data?.updateCurrentUserSettings?.message };
        } catch (error) {
            console.error('Error updating participant merge search settings:', error);
            return { success: false, error: 'Failed to update participant merge search settings' };
        }
    }

    const updateEcmDashboardColumnPreferences = async (page: string, visibleColumns: string[]) => {
        const normalizedPage = typeof page === 'string' ? page.trim() : '';
        if (!normalizedPage) {
            return { success: false, error: 'Invalid ECM dashboard page' };
        }

        const normalizedColumns = coerceStringList(visibleColumns);
        const baseUser = normalizedUser || {};
        const currentPreferences = coerceEcmDashboardColumnPreferences(baseUser?.settings?.ecmDashboardColumnPreferences);
        const nextPreferences = [
            ...currentPreferences.filter((entry) => entry.page !== normalizedPage),
            {
                page: normalizedPage,
                visibleColumns: normalizedColumns
            }
        ].sort((left, right) => left.page.localeCompare(right.page));

        setUser({
            ...baseUser,
            settings: {
                ...(baseUser.settings || {}),
                ecmDashboardColumnPreferences: nextPreferences
            }
        });

        try {
            const result = await updateUserSettingsMutation({
                variables: {
                    settings: {
                        ecmDashboardColumnPreferences: nextPreferences
                    }
                }
            });

            if (result.data?.updateCurrentUserSettings?.responseCode === 200) {
                return { success: true };
            }

            return { success: false, error: result.data?.updateCurrentUserSettings?.message };
        } catch (error) {
            console.error('Error updating ECM dashboard column preferences:', error);
            return { success: false, error: 'Failed to update ECM dashboard column preferences' };
        }
    }

    const updateCentralPlannerPreferences = async (preferences: CentralPlannerPreferences) => {
        const normalizedPreferences = coerceCentralPlannerPreferences(preferences);
        const baseUser = normalizedUser || {};
        setUser({
            ...baseUser,
            settings: {
                ...(baseUser.settings || {}),
                centralPlannerPreferences: normalizedPreferences
            }
        });

        try {
            const result = await updateUserSettingsMutation({
                variables: {
                    settings: {
                        centralPlannerPreferences: normalizedPreferences
                    }
                }
            });

            if (result.data?.updateCurrentUserSettings?.responseCode === 200) {
                return { success: true };
            }

            return { success: false, error: result.data?.updateCurrentUserSettings?.message };
        } catch (error) {
            console.error('Error updating Central Planner preferences:', error);
            return { success: false, error: 'Failed to update Central Planner preferences' };
        }
    }

    const recordRecentParticipantVisit = useCallback(async (visit: RecentParticipantVisit) => {
        if (!visit) return;
        if (typeof visit.odrNumber !== 'number') return;

        const existingUser = userRef.current || {};
        const currentSettings = existingUser.settings || {};
        const currentRecent = coerceRecentParticipants(
            existingUser.recentParticipants ?? currentSettings.recentParticipants
        );

        const filteredRecent = currentRecent.filter(
            (entry: RecentParticipantVisit) => entry.odrNumber !== visit.odrNumber
        );
        const visitRecord = {
            participantId: typeof visit.participantId === 'number' ? visit.participantId : null,
            odrNumber: visit.odrNumber,
            name: visit.name || null,
            visitedAt: visit.visitedAt || new Date().toISOString()
        };

        const updatedRecent = [visitRecord, ...filteredRecent].slice(0, RECENT_PARTICIPANTS_LIMIT);

        const updatedUser = {
            ...existingUser,
            recentParticipants: updatedRecent,
            settings: {
                ...currentSettings,
                recentParticipants: updatedRecent
            }
        };

        const setUserFn = setUserRef.current;
        if (typeof setUserFn === 'function') {
            setUserFn(updatedUser);
        }
        userRef.current = updatedUser;

        try {
            await updateUserSettingsMutation({
                variables: {
                    settings: {
                        recentParticipants: updatedRecent
                    }
                }
            });
        } catch (error) {
            console.error('Error updating recent participants:', error);
        }
    }, [updateUserSettingsMutation]);

    return {
        user: normalizedUser,
        setUser,
        isUserLoading,
        setIsUserLoading,
        logout,
        updateAdminFavorites,
        updateSystemSettingsFavorites,
        updateDashboardPreference,
        updateReportFavorites,
        updateEcmDashboardColumnPreferences,
        updateCentralPlannerPreferences,
        updateParticipantMergeSearchSettings,
        recordRecentParticipantVisit,
        ecmDashboardColumnPreferences,
        ecmDashboardColumnPreferenceMap,
        centralPlannerPreferences,
        participantMergeSearchSettings,
        recentParticipants
    };
}
